import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from "react-router-dom";

import StartScreen from "./StartScreen";
import QuizView from "./QuizView";
import { quizData } from "./data/quizData";
import "./App.css";
import ResultPage from "./components/ResultPage";
import SharePage from "./components/SharePage";
import ScrollToTop from "./components/ScrollToTop";
import Footer from "./components/Footer";
import { preloadImages } from "./utils/preloadImages";

function AppContent() {
  const [isStarting, setIsStarting] = useState(false);


  const navigate = useNavigate();

  // Initialize state from sessionStorage to allow for basic persistence on refresh
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const savedIndex = sessionStorage.getItem("currentQuestionIndex");
    return savedIndex ? parseInt(savedIndex, 10) : 0;
  });
  const [userAnswers, setUserAnswers] = useState(() => {
    const savedAnswers = sessionStorage.getItem("userAnswers");
    return savedAnswers ? JSON.parse(savedAnswers) : {};
  });
  const [quizResult, setQuizResult] = useState(() => {
    const savedResult = sessionStorage.getItem("quizResult");
    return savedResult ? JSON.parse(savedResult) : null;
  });

  // Persist quiz state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("currentQuestionIndex", currentQuestionIndex.toString());
  }, [currentQuestionIndex]);

  useEffect(() => {
    sessionStorage.setItem("userAnswers", JSON.stringify(userAnswers));
  }, [userAnswers]);

  useEffect(() => {
    if (quizResult) {
      sessionStorage.setItem("quizResult", JSON.stringify(quizResult));
    } else {
      // If quizResult becomes null (e.g., on restart), remove it from storage
      sessionStorage.removeItem("quizResult");
    }
  }, [quizResult]);

  // Function to begin the quiz
  const handleStartQuiz = async () => {
    setIsStarting(true);
    await preloadImages(quizData.map((q) => `/assets/images/${q.image}`));
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setQuizResult(null);
    // Clear session storage for a fresh start
    sessionStorage.removeItem("currentQuestionIndex");
    sessionStorage.removeItem("userAnswers");
    sessionStorage.removeItem("quizResult");

    setIsStarting(false);
    navigate("/quiz");
  };

  // Function to handle answer submission
  const handleAnswerSubmit = (questionId, answer) => {
    const newAnswers = {
      ...userAnswers,
      [questionId]: answer,
    };
    setUserAnswers(newAnswers);

    if (currentQuestionIndex < quizData.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    } else {
      // Pass the latest answers directly to calculateAndSetResult
      calculateAndSetResult(newAnswers);
      // Navigation to /results will happen inside calculateAndSetResult
    }
  };

  const previousQuestion = () => {
    setCurrentQuestionIndex((prevIndex) => Math.max(0, prevIndex - 1)); // Prevent going below 0
  };

  // Function to calculate and set the quiz result
  const calculateAndSetResult = (currentAnswers) => {
    const answers = currentAnswers; // Use the most up-to-date answers
    let medicationMatch = "Semaglutide"; // Default fallback

    // --- Implement your recommendation logic here based on 'answers' ---
    if ((answers["q1"] === "25-50 lbs" || answers["q1"] === "50+ lbs") && answers["q2"] === "fast" && answers["q3"] === "$400+" && answers["q4"] === "Yes") {
      medicationMatch = "Tirzepatide";
    } else if (answers["q1"] === "10-25 lbs" && answers["q3"] === "$200-$400" && answers["q4"] === "Yes") {
      medicationMatch = "Tirzepatide";
    } else if (answers["q1"] === "10-25 lbs" && answers["q3"] === "$200-$400" && answers["q4"] === "No") {
      medicationMatch = "Oral Semaglutide Drops";
    }

    if (answers["q3"] === "$400+" && answers["q2"] === "fast") {
      medicationMatch = "Retatrutide";
    } else {
      medicationMatch = "Semaglutide";
    }

    const medicineImage =
      medicationMatch === "Tirzepatide"
        ? "tirzepatide.webp"
        : medicationMatch === "Retatrutide"
          ? "retatrutide.webp"
          : medicationMatch === "Oral Semaglutide Drops"
            ? "oral-semaglutide-drops.webp"
            : "semaglutide.webp";

    const resultData = {
      medicationName: medicationMatch,
      medicineImage,
      weightGoal: answers["q1"] || "your desired weight",
      speedPreference: answers["q2"] || "your preferred speed",
      budget: answers["q3"] || "your budget",
      injectionPreference: answers["q4"] === "Yes" ? "okay with injections" : "prefer no injections",
      biggestChallenge: answers["q12"] || "uncertainty", // Assuming q12 exists
      readinessLevel: answers["q11"] || "very committed", // Assuming q11 exists
    };
    setQuizResult(resultData);
    navigate("/results"); // Navigate after the result is set
  };

  // Function to restart the quiz
  const handleRestartQuiz = () => {
    // Resetting state is handled by handleStartQuiz if user goes through start screen
    // For direct restart from results/share:
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setQuizResult(null);
    sessionStorage.removeItem("currentQuestionIndex");
    sessionStorage.removeItem("userAnswers");
    sessionStorage.removeItem("quizResult");
    navigate("/");
  };

  // Function to navigate to SharePage from ResultPage
  const handleNavigateToShare = () => {
    if (quizResult) {
      // Ensure there's a result to share
      navigate("/share");
    } else {
      // This case should ideally not be hit if called from ResultPage correctly
      navigate("/"); // Fallback to home
    }
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<StartScreen isLoading={isStarting} onStartQuiz={handleStartQuiz} />} />
        <Route
          path="/quiz"
          element={
            quizData && quizData.length > 0 ? (
              <>
                <QuizView
                  quizData={quizData}
                  currentQuestionIndex={currentQuestionIndex}
                  userAnswers={userAnswers} // For displaying previously selected answers
                  onAnswerSubmit={handleAnswerSubmit}
                  previousQuestion={previousQuestion}
                  totalQuestions={quizData.length} // For progress indication
                />
                <Footer />
              </>
            ) : (
              <div className="min-h-screen bg-gray-100 flex items-center justify-center">Loading quiz data...</div>
            )
          }
        />
        <Route
          path="/results"
          element={
            quizResult ? (
              <>
                <ResultPage
                  result={quizResult}
                  onRestartQuiz={handleRestartQuiz}
                  onNavigateToShare={handleNavigateToShare}
                  setQuizResult={setQuizResult}
                // The original ResultPage received setCurrentView and setQuizResult.
                // onRestartQuiz replaces the restart functionality of setCurrentView.
                // onNavigateToShare replaces the share navigation part of setCurrentView.
                // setQuizResult is not passed as ResultPage typically doesn't modify the result itself,
                // but triggers actions (like restart) that do.
                />
                <Footer />
              </>
            ) : (
              // If there's no quizResult, redirect to the start page.
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/share"
          element={
            quizResult ? (
              <>
                <SharePage result={quizResult} onRestartQuiz={handleRestartQuiz} />
                <Footer />
              </>
            ) : (
              // If there's no quizResult, redirect to the start page.
              <Navigate to="/" replace />
            )
          }
        />
        {/* Fallback route for any undefined paths */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// The main App component now simply sets up the Router
export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}
